import React, { useState, useRef } from "react";
import styled from "styled-components";
import ADD_RED_SVG from "assets/images/add_red.svg";
import IMG_SVG from "assets/images/img.svg";
import UPLOAD_SVG from "assets/images/upload.svg";
import { sentence } from "shared/types/Report";
import PLAY_SVG from "assets/images/play.svg";
import STOP_SVG from "assets/images/stop.svg";
import DELETE_SVG from "assets/images/delete.svg";
import {
    EditorTableWrap, EditorTable, EditorThead, EditorTbody, EditorTfoot, EditorTrHead, EditorTr, EditorTd, EditorTh,
    EditorScroll, EditorInput, EditorIconBtn, EditorCheck, EditorThumb, EditorAudioRow, TableEmpty
} from "../homeworkUi";

interface SentenceTableProps {
  list: sentence[];
  selectedList: number[];
  onAddClickHandler: () => void;
  onChangeHandler: (index: number, name: string, value: string) => void;
  onSelectHandler: (id: number, value: boolean) => void;
  onSelectAllClickHandler: (value: boolean) => void;
  audioInputRefs: React.RefObject<(HTMLInputElement | null)[]>;
  imageInputRefs: React.RefObject<(HTMLInputElement | null)[]>;
  onImageChangeHandler: (index: number, event: React.ChangeEvent<HTMLInputElement>) => void;
  onImageInputClickHandler: (index: number) => void;
  onAudioChangeHandler: (index: number, event: React.ChangeEvent<HTMLInputElement>) => void;
  onAudioInputClickHandler: (index: number) => void;
  onFileClickHandler: (url: string, index: number, type: string) => void;
  onAudioDeleteHandler: (index: number) => void;
}

const SentenceTable = ({
  list,
  selectedList,
  onAddClickHandler,
  onChangeHandler,
  onSelectAllClickHandler,
  onSelectHandler,
  imageInputRefs,
  onImageInputClickHandler,
  onImageChangeHandler,
  onFileClickHandler,
  audioInputRefs,
  onAudioInputClickHandler,
  onAudioChangeHandler,
  onAudioDeleteHandler,
}: SentenceTableProps) => {
  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
  const [isPlayingList, setIsPlayingList] = useState(list.map(() => false));

  const handlePlayPause = (index: number) => {
    const audioRef = audioRefs.current[index];
    if (audioRef) {
      if (isPlayingList[index]) {
        audioRef.pause();
      } else {
        audioRef.play();
      }
      setIsPlayingList((prev) => {
        const newPlayingList = [...prev];
        newPlayingList[index] = !newPlayingList[index];
        return newPlayingList;
      });
    }
  };

  const renderImagePreview = (item: sentence, index: number) => {
    if (item.image_preview) {
      return (
        <EditorThumb
          src={item.image_preview}
          alt="preview"
          onClick={() => {
            if (item.image_preview) {
              onFileClickHandler(item.image_preview, index, "image");
            }
          }}
        />
      );
    }
    if (item.image) {
      const imageUrl = typeof item.image === "string" ? item.image : "";
      return (
        <EditorThumb
          src={imageUrl}
          alt="preview"
          onClick={() => {
            if (imageUrl) {
              onFileClickHandler(imageUrl, index, "image");
            }
          }}
        />
      );
    }
    return (
      <>
        <HiddenInput
          type="file"
          accept="image/*"
          ref={(el) => {
            if (imageInputRefs.current) {
              imageInputRefs.current[index] = el;
            }
          }}
          onChange={(e) => onImageChangeHandler(index, e)}
        />
        <EditorIconBtn src={IMG_SVG} alt="add_img" onClick={() => onImageInputClickHandler(index)} />
      </>
    );
  };

  const renderAudioPreview = (item: sentence, index: number) => {
    if (item.audio) {
      const audioUrl = typeof item.audio === "string" ? item.audio : "";
      return (
        <EditorAudioRow>
          <audio ref={(el) => (audioRefs.current[index] = el)} src={audioUrl} />
          <EditorIconBtn
            src={isPlayingList[index] ? STOP_SVG : PLAY_SVG}
            alt="play_img"
            onClick={() => handlePlayPause(index)}
          />
          <EditorIconBtn
            src={DELETE_SVG}
            alt="delete_audio"
            onClick={() => onAudioDeleteHandler(index)}
          />
        </EditorAudioRow>
      );
    }
    return (
      <>
        <HiddenInput
          type="file"
          accept="audio/*"
          ref={(el) => {
            if (audioInputRefs.current) {
              audioInputRefs.current[index] = el;
            }
          }}
          onChange={(e) => onAudioChangeHandler(index, e)}
        />
        <EditorIconBtn src={UPLOAD_SVG} alt="add_audio" onClick={() => onAudioInputClickHandler(index)} />
      </>
    );
  };

  const viewSentenceList = () => {
    if (!list.length) {
      return (
        <EditorTr>
          <EditorTd colSpan={6}>
            <TableEmpty>아직 문장이 없습니다. 아래 + 버튼으로 추가하거나 일괄등록을 사용하세요.</TableEmpty>
          </EditorTd>
        </EditorTr>
      );
    }
    return list.map((item, index) => (
      <EditorTr key={index + 1}>
        <EditorTd style={{ width: "6%" }}>{index + 1}</EditorTd>
        <EditorTd style={{ width: "36%" }}>
          <EditorInput
            name="sentence"
            value={item.sentence}
            onChange={(e) => onChangeHandler(index, e.target.name, e.target.value)}
            placeholder="전체 문장을 입력해 주세요"
          />
        </EditorTd>
        <EditorTd style={{ width: "30%" }}>
          <EditorInput
            name="kor"
            value={item.kor}
            onChange={(e) => onChangeHandler(index, e.target.name, e.target.value)}
          />
        </EditorTd>
        <EditorTd style={{ width: "10%" }}>{renderAudioPreview(item, index)}</EditorTd>
        <EditorTd style={{ width: "10%" }}>{renderImagePreview(item, index)}</EditorTd>
        <EditorTd style={{ width: "8%" }}>
          <EditorCheck
            type="checkbox"
            checked={selectedList.includes(item.id)}
            onChange={(e) => onSelectHandler(item.id, e.target.checked)}
          />
        </EditorTd>
      </EditorTr>
    ));
  };

  return (
    <EditorTableWrap>
      <EditorTable>
        <EditorThead>
          <EditorTrHead>
            <EditorTh style={{ width: "6%" }}>번호</EditorTh>
            <EditorTh style={{ width: "36%" }}>문장</EditorTh>
            <EditorTh style={{ width: "30%" }}>해석</EditorTh>
            <EditorTh style={{ width: "10%" }}>음원</EditorTh>
            <EditorTh style={{ width: "10%" }}>이미지</EditorTh>
            <EditorTh style={{ width: "8%" }}>
              <EditorCheck type="checkbox" onChange={(e) => onSelectAllClickHandler(e.target.checked)} />
            </EditorTh>
          </EditorTrHead>
        </EditorThead>
      </EditorTable>
      <EditorScroll>
        <EditorTable>
          <EditorTbody>{viewSentenceList()}</EditorTbody>
        </EditorTable>
      </EditorScroll>
      <EditorTable>
        <EditorTfoot>
          <EditorTr>
            <EditorTd colSpan={6}>
              <EditorIconBtn src={ADD_RED_SVG} alt="add" onClick={onAddClickHandler} />
            </EditorTd>
          </EditorTr>
        </EditorTfoot>
      </EditorTable>
    </EditorTableWrap>
  );
};

const HiddenInput = styled.input`
  display: none;
`;

export default SentenceTable;
